import React from 'react';
import AiDisclaimer from './AiDisclaimer';

const RATING_POSITIVE = new Set(['GREAT', 'GOOD']);

/**
 * Two-sided trade analysis panel for the S3 use case.
 *
 * @param {Object}   props
 * @param {Object}   props.data - TradeAnalysisResponse
 * @param {string}   props.data.leagueId
 * @param {string}   props.data.yourRating   - GREAT | GOOD | POOR | TERRIBLE
 * @param {string}   props.data.theirRating
 * @param {Array}    props.data.givingAnalysis   - { playerName, position, team, impact }[]
 * @param {Array}    props.data.receivingAnalysis
 * @param {string}   props.data.summary
 * @param {string}   props.data.dataAsOf
 * @param {Function} [props.onClose]
 */
export default function TradeAnalysisPanel({ data, earlyAccess, onClose }) {
  const { yourRating, theirRating, givingAnalysis, receivingAnalysis, summary, dataAsOf, note } = data;

  return (
    <div className="trade-analysis-panel">
      <div className="ai-suggestion-panel-header">
        <span className="ai-suggestion-panel-title">✦ Trade Analysis</span>
        {onClose && (
          <button className="ai-suggestion-panel-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        )}
      </div>

      {/* Overall grade of the DEAL for each side -- kept separate from the You Give / You
          Receive player columns below, since pairing a grade badge with "You Give" or "You
          Receive" reads as if it rates the players in that column, when it actually rates how
          the trade nets out for that party (e.g. "GREAT" here means great FOR YOU, not that the
          players you're giving away are great). */}
      <p className="ai-context-note">Factors in recent form and player news.</p>

      <div className="trade-analysis-grades">
        <RatingBadge label="For You" rating={yourRating} />
        <RatingBadge label="For Them" rating={theirRating} />
      </div>

      <div className="trade-analysis-columns">
        <TradeColumn heading="You Give" players={givingAnalysis} />
        <TradeColumn heading="You Receive" players={receivingAnalysis} />
      </div>

      {summary && <p className="trade-analysis-summary">{summary}</p>}

      {note && <p className="trade-analysis-note">{note}</p>}

      <AiDisclaimer dataAsOf={dataAsOf} earlyAccess={earlyAccess} />
    </div>
  );
}

function RatingBadge({ label, rating }) {
  const isPositive = RATING_POSITIVE.has(rating);
  return (
    <div className="trade-analysis-grade">
      <span className="trade-analysis-grade-label">{label}</span>
      <span className={`trade-rating-badge ${isPositive ? 'trade-rating-badge--positive' : 'trade-rating-badge--negative'}`}>
        {rating}
      </span>
    </div>
  );
}

function TradeColumn({ heading, players }) {
  return (
    <div className="trade-analysis-column">
      <p className="trade-analysis-column-heading">{heading}</p>
      <ul className="trade-analysis-player-list">
        {(players || []).map((p, i) => (
          <li key={i} className="trade-analysis-player-item">
            <strong>{p.playerName}</strong>
            <span className="trade-analysis-player-meta"> · {p.position}, {p.team}</span>
            <p className="trade-analysis-player-impact">{p.impact}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}